<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.progress_evaluation'),
                        href: meta.links.index,
                    },
                    {
                        name: progressEvaluation.category_name,
                        href: meta.links.category_index,
                    },
                    {
                        name: group.name,
                        href: meta.links.progress_evaluation_group_index,
                    },
                ]"
            />
        </template>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 w-40 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.score') }}<br />
                                    ({{ progressEvaluationComparison.first.date_display }})
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.score') }}<br />
                                    ({{ progressEvaluationComparison.second.date_display }})
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.improvement_percentage') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="hover:bg-gray-200" v-for="score in progressEvaluationComparison.scores">
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <span class="text-gray-400" v-if="score.school_support_special_education"> # </span>
                                    {{ score.student }}
                                </td>
                                <td
                                    class="px-4 sm:px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    {{ score.first_score }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ score.second_score }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <div class="flex justify-start">
                                        <span v-if="score.improvement === '-'">-</span>
                                        <template v-else>
                                            <IconEqual v-if="score.improvement == 0" style="color: #a0aec0" />
                                            <IconTrendingUp
                                                class="mr-3"
                                                style="color: #68d391"
                                                v-else-if="score.improvement > 0"
                                            />
                                            <IconTrendingDown class="mr-3" style="color: #fc8181" v-else />
                                            {{ score['improvement'] }}%
                                        </template>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="mt-5">
            {{ $t('messages.summary') }}
            ({{ $tc('messages.X_students_improve', progressEvaluationComparison.improvingStudentsCount) }})
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    &nbsp;
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 w-40 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ progressEvaluationComparison.first.date_display }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ progressEvaluationComparison.second.date_display }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="hover:bg-gray-200" v-for="item in progressEvaluationComparison.summaryItems">
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ item.title }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ item.first }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ item.second }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="mt-5">
            {{ $t('messages.success_criteria') }}

            <div class="md:grid xl:grid-cols-2 md:gap-6">
                <div class="flex flex-col mt-5">
                    <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                        <div
                            class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                        >
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th
                                            class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            &nbsp;
                                        </th>
                                        <th
                                            class="px-6 py-3 bg-gray-50 w-40 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ progressEvaluationComparison.first.date_display }}
                                        </th>
                                        <th
                                            class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ progressEvaluationComparison.second.date_display }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr class="hover:bg-gray-200">
                                        <td
                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ $t('messages.pass_percentage') }} >
                                            {{ progressEvaluation.passing_students_percent }}%
                                        </td>
                                        <td
                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            <IconThumbUpFilled
                                                class="w-6 h-6 text-green-400 mr-3 inline-block"
                                                style="color: #68d391"
                                                v-if="
                                                    progressEvaluationComparison.firstPassesPercentage >
                                                    progressEvaluation.passing_students_percent
                                                "
                                            />

                                            <IconThumbDownFilled
                                                class="w-6 h-6 text-red-400 mr-3 inline-block"
                                                style="color: #fc8181"
                                                v-else
                                            />

                                            <template v-if="progressEvaluationComparison.firstPassesPercentage">
                                                {{ progressEvaluationComparison.firstPassesPercentage.toFixed(2) }}%
                                            </template>
                                        </td>
                                        <td
                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            <IconThumbUpFilled
                                                class="w-6 h-6 text-green-400 mr-3 inline-block"
                                                style="color: #68d391"
                                                v-if="
                                                    progressEvaluationComparison.secondPassesPercentage >
                                                    progressEvaluation.passing_students_percent
                                                "
                                            />

                                            <IconThumbDownFilled
                                                class="w-6 h-6 text-red-400 mr-3 inline-block"
                                                style="color: #fc8181"
                                                v-else
                                            />
                                            <template v-if="progressEvaluationComparison.secondPassesPercentage">
                                                {{ progressEvaluationComparison.secondPassesPercentage.toFixed(2) }}%
                                            </template>
                                        </td>
                                    </tr>
                                    <tr class="hover:bg-gray-200">
                                        <td
                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ $t('messages.school_support_special_education') }}
                                        </td>
                                        <td
                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            <span
                                                v-if="
                                                    progressEvaluationComparison.extra.special_education
                                                        .firstPassesPercentage === null
                                                "
                                            >
                                                &nbsp;
                                            </span>
                                            <IconThumbUpFilled
                                                class="w-6 h-6 text-green-400 mr-3 inline-block"
                                                style="color: #68d391"
                                                v-else-if="
                                                    progressEvaluationComparison.extra.special_education
                                                        .firstPassesPercentage >
                                                    progressEvaluation.passing_students_percent
                                                "
                                            />
                                            <IconThumbDownFilled
                                                class="w-6 h-6 text-red-400 mr-3 inline-block"
                                                style="color: #fc8181"
                                                v-else
                                            />
                                            <template
                                                v-if="
                                                    progressEvaluationComparison.extra.special_education
                                                        .firstPassesPercentage !== null
                                                "
                                            >
                                                {{
                                                    progressEvaluationComparison.extra.special_education.firstPassesPercentage.toFixed(
                                                        2,
                                                    )
                                                }}%
                                            </template>
                                        </td>
                                        <td
                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            <span
                                                v-if="
                                                    progressEvaluationComparison.extra.special_education
                                                        .secondPassesPercentage === null
                                                "
                                            >
                                                &nbsp;
                                            </span>
                                            <IconThumbUpFilled
                                                class="w-6 h-6 text-green-400 mr-3 inline-block"
                                                style="color: #68d391"
                                                v-else-if="
                                                    progressEvaluationComparison.extra.special_education
                                                        .secondPassesPercentage >
                                                    progressEvaluation.passing_students_percent
                                                "
                                            />
                                            <IconThumbDownFilled
                                                class="w-6 h-6 text-red-400 mr-3 inline-block"
                                                style="color: #fc8181"
                                                v-else
                                            />
                                            <template
                                                v-if="
                                                    progressEvaluationComparison.extra.special_education
                                                        .secondPassesPercentage !== null
                                                "
                                            >
                                                {{
                                                    progressEvaluationComparison.extra.special_education.secondPassesPercentage.toFixed(
                                                        2,
                                                    )
                                                }}%
                                            </template>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="flex flex-col mt-5">
                    <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                        <div
                            class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                        >
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th
                                            class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            &nbsp;
                                        </th>
                                        <th
                                            class="px-6 py-3 bg-gray-50 w-40 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            &nbsp;
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr class="hover:bg-gray-200">
                                        <td
                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ $t('messages.average_improvement') }}
                                            (>{{ progressEvaluation.average_improvement }}%)
                                        </td>
                                        <td
                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            <IconThumbUpFilled
                                                class="w-6 h-6 text-green-400 mr-3 inline-block"
                                                style="color: #68d391"
                                                v-if="
                                                    progressEvaluationComparison.averageImprovement >
                                                    progressEvaluation.average_improvement
                                                "
                                            />
                                            <IconThumbDownFilled
                                                class="w-6 h-6 text-red-400 mr-3 inline-block"
                                                style="color: #fc8181"
                                                v-else
                                            />
                                            <template v-if="progressEvaluationComparison.averageImprovement">
                                                {{ progressEvaluationComparison.averageImprovement.toFixed(2) }}%
                                            </template>
                                        </td>
                                    </tr>
                                    <tr class="hover:bg-gray-200">
                                        <td
                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ $t('messages.school_support_special_education') }}
                                            (>{{ progressEvaluation.average_improvement }}%)
                                        </td>
                                        <td
                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            <IconThumbUpFilled
                                                class="w-6 h-6 text-green-400 mr-3 inline-block"
                                                style="color: #68d391"
                                                v-if="
                                                    progressEvaluationComparison.extra.special_education
                                                        .averageImprovement > progressEvaluation.average_improvement
                                                "
                                            />
                                            <IconThumbDownFilled
                                                class="w-6 h-6 text-red-400 mr-3 inline-block"
                                                style="color: #fc8181"
                                                v-else
                                            />
                                            <template
                                                v-if="
                                                    progressEvaluationComparison.extra.special_education
                                                        .averageImprovement
                                                "
                                            >
                                                {{
                                                    progressEvaluationComparison.extra.special_education.averageImprovement.toFixed(
                                                        2,
                                                    )
                                                }}%
                                            </template>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-3">
                <IconThumbUpFilled
                    class="w-6 h-6 text-green-400 mr-3 inline-block"
                    style="color: #68d391"
                    v-if="progressEvaluationComparison.desiredImprovement.accomplishedDesiredImprovement"
                />
                <IconThumbDownFilled class="w-6 h-6 text-red-400 mr-3 inline-block" style="color: #fc8181" v-else />
                {{ progressEvaluationComparison.desiredImprovement.message }}
            </div>
        </div>

        <div class="pt-5" id="chart">
            <apexchart
                type="bar"
                height="350"
                :series="progressEvaluationComparison.scoresChartValuesData.series"
                :options="progressEvaluationComparison.scoresChartValuesData"
            >
            </apexchart>
        </div>

        <div class="flex space-x-2">
            <AppButton href="?filter=school_support_special_education#chart">
                {{ $t('messages.school_support_special_education') }}
            </AppButton>
            <AppButton href="?filter=study_variable#chart">
                {{ $t('messages.study_variable') }}
            </AppButton>
            <!--            @if(request()->filled('filter'))-->
            <AppButton href="?filter=#chart">
                {{ $t('messages.all') }}
            </AppButton>
            <!--            @endif-->
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import { IconEqual, IconThumbDownFilled, IconThumbUpFilled, IconTrendingDown, IconTrendingUp } from '@tabler/icons-vue';

export default {
    components: {
        AppButton,
        PanelPage,
        IconEqual,
        IconTrendingUp,
        IconTrendingDown,
        IconThumbDownFilled,
        IconThumbUpFilled,
    },
    layout: Layout,
    name: 'Edit',
    props: ['meta', 'group', 'progressEvaluation', 'progressEvaluationComparison'],
};
</script>
